import * as React from 'react';
import { connect } from 'react-redux';
import { ChangeEvent } from 'react';
import { getOrderedValue, getOrderValue, ItemOrderDraft, OrderedSku, OrderStocks } from '../../domain/Order';
import './OrderDetails.css';
import DispatchProps, { mapDispatchToProps } from '../../application/core/DispatchProps';
import { EstimationMonthSum, getEstimationForSku } from '../../domain/estimations/Estimations';
import Product, { getSkuCheck, getStockValue, isEnabledFromStock, Item } from '../../domain/Product';
import isDisabledFromEstimation from '../../domain/orders/isDisabledFromEstimation';
import getLiability from '../../domain/orders/getLiability';
import preventExpotential, { removeExpotential } from '../preventExpotential';
import OrderChangeRequested from '../../application/order/events/OrderChangeRequested';
import Tooltip from '../tooltip/Tooltip';
import formatNumber from '../formatNumber';

interface Props {
    model: ItemOrderDraft[];
    savedModel: ItemOrderDraft[];
    month: string;
    packageSize: number;
    estimations: EstimationMonthSum;
    product: Product;
    isLiabilityMet: boolean;
    orderedSku: OrderedSku;
}

class OrderDetails extends React.PureComponent<Props & DispatchProps> {
    public render(): JSX.Element {
        return (
            <div className="order-details">
                {this.props.product.items.map((item: Item) => (
                    <div className="order-details-row" key={item.number}>
                        <div className="order-details-size">
                            <span>{item.size}</span>
                        </div>
                        <div className="order-details-estimated">
                            <Tooltip
                                size="small"
                                enabled={true}
                                message={'items checkout level: ' + this.getCheckoutLabel(this.props.product, item.number)}
                            >
                                <span>
                                    {formatNumber(
                                        getEstimationForSku(this.props.estimations, this.props.product.articleId, item.number),
                                        0
                                    )}
                                </span>
                            </Tooltip>
                        </div>
                        <div className="order-details-ordered">
                            <span>{formatNumber(getOrderedValue(item.number, this.props.orderedSku), 0)}</span>
                        </div>
                        <div className="order-details-liability">
                            <span>
                                {getLiability(
                                    getOrderedValue(item.number, this.props.orderedSku),
                                    getEstimationForSku(this.props.estimations, this.props.product.articleId, item.number)
                                )}
                            </span>
                        </div>
                        <div>
                            <input
                                disabled={isDisabledFromEstimation(
                                    getOrderedValue(item.number, this.props.orderedSku),
                                    getEstimationForSku(this.props.estimations, this.props.product.articleId, item.number)
                                )}
                                className={this.getClass(item.number, 'toOrderFromEstimation')}
                                type="number"
                                min="0"
                                max={getEstimationForSku(this.props.estimations, this.props.product.articleId, item.number)}
                                step={this.props.packageSize}
                                value={this.removeLeadingZeros(getOrderValue(this.props.model, item.number, 'toOrderFromEstimation'))}
                                // tslint:disable:jsx-no-lambda
                                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                                    this.orderFromEstimationUpdated(
                                        event.target.value,
                                        item.number,
                                        item.variantCode,
                                        this.props.packageSize,
                                        'toOrderFromEstimation'
                                    )
                                }
                                onBlur={(event: ChangeEvent<HTMLInputElement>): void =>
                                    this.orderFromEstimationUpdated(
                                        event.target.value,
                                        item.number,
                                        item.variantCode,
                                        this.props.packageSize,
                                        'toOrderFromEstimation',
                                        true
                                    )
                                }
                                onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>): void => preventExpotential(event)}
                                onPaste={(event: React.ClipboardEvent<HTMLInputElement>): void => {
                                    removeExpotential(event, event.clipboardData.getData('Text'));
                                }}
                            />
                        </div>
                        <div className="order-details-stock-available">
                            <span>{getStockValue(this.props.product, item.number)}</span>
                        </div>
                        <div className="order-details-stock-order">
                            <input
                                disabled={!isEnabledFromStock(this.props.isLiabilityMet, this.props.product.isFreeToOrder)}
                                className={this.getClass(item.number, 'toOrderFromStock')}
                                type="number"
                                min="0"
                                max={Number.MAX_SAFE_INTEGER}
                                step={this.props.packageSize}
                                value={this.removeLeadingZeros(getOrderValue(this.props.model, item.number, 'toOrderFromStock'))}
                                // tslint:disable:jsx-no-lambda
                                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                                    this.orderUpdated(
                                        event.target.value,
                                        item.number,
                                        item.variantCode,
                                        this.props.packageSize,
                                        'toOrderFromStock'
                                    )
                                }
                                onBlur={(event: ChangeEvent<HTMLInputElement>): void =>
                                    this.orderUpdated(
                                        event.target.value,
                                        item.number,
                                        item.variantCode,
                                        this.props.packageSize,
                                        'toOrderFromStock',
                                        true
                                    )
                                }
                                onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>): void => preventExpotential(event)}
                                onPaste={(event: React.ClipboardEvent<HTMLInputElement>): void => {
                                    removeExpotential(event, event.clipboardData.getData('Text'));
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    private getClass = (sku: string, field: keyof OrderStocks): string => {
        const currentValue = getOrderValue(this.props.model, sku, field);
        const savedValue = getOrderValue(this.props.savedModel, sku, field);
        let result = 'order-details-month-input';
        if (currentValue !== savedValue) {
            result += ' not-saved';
        } else if (currentValue > 0 && currentValue === savedValue) {
            result += ' draft';
        }
        return result;
    };

    private removeLeadingZeros = (value: number): string => {
        return value === 0 ? '' : value.toString();
    };

    private getCheckoutLabel = (product: Product, sku: string): string => {
        return getSkuCheck(product, sku) === undefined ? '?' : getSkuCheck(product, sku)!.toString();
    };

    private orderUpdated = (
        value: string,
        sku: string,
        variantCode: string,
        packageSize: number,
        field: keyof OrderStocks,
        validate?: boolean
    ): void => {
        this.props.dispatch(new OrderChangeRequested(this.props.product, Number(value), sku, variantCode, field, validate));
    };

    private orderFromEstimationUpdated = (
        value: string,
        sku: string,
        variantCode: string,
        packageSize: number,
        field: keyof OrderStocks,
        validate?: boolean
    ): void => {
        this.props.dispatch(new OrderChangeRequested(this.props.product, Number(value), sku, variantCode, field, validate));
    };
}

export default connect(mapDispatchToProps)(OrderDetails);



// WEBPACK FOOTER //
// ./src/view/order/OrderDetails.tsx